import { methodsName, payment_method } from 'src/constants/ShoppingCart';
import {
    PanelPaymentMethod,
    PanelPaymentStatus,
    PanelTransactionOrigin,
    PanelTransactionsQuery,
    PanelTransactionsSummary,
} from './panel_transactions.types';

/** Tamaño de página por defecto. El backend acota `page_size` a 100. */
export const DEFAULT_PAGE_SIZE = 20;

/** Opciones del selector "Mostrar N por página". */
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * Primer mes con datos confiables (`YYYY-MM`). Igual que `HISTORY_CUTOFF_DATE`
 * del API: el listado no trae operaciones anteriores a enero de 2026.
 */
export const HISTORY_CUTOFF_YEAR_MONTH = '2026-01';

/** Valor del filtro de periodo cuando no se acota a un mes. */
export const PERIOD_ALL_VALUE = '';

/** Periodo por defecto: el histórico disponible desde el corte, sin acotar a un mes. */
export const DEFAULT_YEAR_MONTH = PERIOD_ALL_VALUE;

/** Etiquetas del estado de pago. */
export const PAYMENT_STATUS_LABELS: Record<PanelPaymentStatus, string> = {
    in_process: 'En proceso',
    successful: 'Exitoso',
    failed: 'Fallido',
};

const EMPTY_BUCKET = { count: 0, amount: 0 };

/** Resumen vacío: los tres cubos en cero. La UI no inventa cifras. */
export const EMPTY_SUMMARY: PanelTransactionsSummary = {
    in_process: { ...EMPTY_BUCKET },
    successful: { ...EMPTY_BUCKET },
    failed: { ...EMPTY_BUCKET },
};

/**
 * Etiqueta de la pasarela (`payment_gateways_payments.payment_gateway`). Los ids
 * son los que el carrito comercial envía al crear el cobro, así que se leen de
 * ahí en lugar de repetirse como números sueltos.
 *
 * Los nombres son del canal, no de la forma de pago: las pasarelas 3, 4, 5, 6
 * y 7 se nombran "OpenPay". La 7 es Destacado por suscripción
 * (`payment_gateways_subscriptions`).
 */
export const PAYMENT_GATEWAY_LABELS: Record<number, string> = {
    [payment_method[methodsName.PAYPAL]]: 'Paypal',
    [payment_method[methodsName.DINERO_MAIL]]: 'Dinero Mail',
    [payment_method[methodsName.CARD]]: 'OpenPay',
    [payment_method[methodsName.TRANSFER]]: 'OpenPay',
    [payment_method[methodsName.CARD_3DS]]: 'OpenPay',
    [payment_method[methodsName.CASH]]: 'OpenPay',
    7: 'OpenPay',
    [payment_method[methodsName.DIRECT]]: 'Pago Directo (PCOM)',
    [payment_method[methodsName.MERCADOPAGO]]: 'MercadoPago',
};

/**
 * Etiquetas del filtro: las mismas pasarelas y el mismo orden que el selector
 * del carrito comercial. El backend traduce cada valor a las pasarelas que le
 * corresponden (`TransactionsEntity.PAYMENT_GROUPS`). OpenPay incluye también
 * la pasarela 5 (carrito del usuario): con alcance global esas filas entran;
 * con alcance propio el recorte por comercial las deja fuera.
 */
export const PAYMENT_METHOD_LABELS: Record<PanelPaymentMethod, string> = {
    mercado_pago: 'MercadoPago',
    openpay: 'OpenPay',
    paypal: 'Paypal',
    pago_directo: 'Pago Directo (PCOM)',
};

/** Opciones del filtro, en el orden del carrito comercial. */
export const PAYMENT_METHOD_OPTIONS: PanelPaymentMethod[] = [
    'mercado_pago',
    'openpay',
    'paypal',
    'pago_directo',
];

/**
 * Forma de pago dentro de la pasarela (`payment_gateways_payments.method`), la
 * segunda mitad de la celda "Monto y método" ("OpenPay · Tarjeta").
 *
 * En OpenPay y MercadoPago se colapsa a las tres formas del carrito: Tarjeta,
 * Transferencia y Depósito en efectivo. Los valores genéricos —los que solo
 * nombran la pasarela, como `mercadopago` cuando el cliente todavía no pagó el
 * link— se traducen igual que su pasarela para que la celda no repita el nombre.
 */
export const PAYMENT_TYPE_LABELS: Record<string, string> = {
    mercadopago: 'MercadoPago',
    mercado_pago: 'MercadoPago',
    paypal: 'Paypal',
    pago_directo: 'Pago Directo (PCOM)',
    card: 'Tarjeta',
    credit_card: 'Tarjeta',
    credit_card_mercadopago: 'Tarjeta',
    debit_card_mercadopago: 'Tarjeta',
    bank_account: 'Transferencia',
    bank_transfer_mercadopago: 'Transferencia',
    store: 'Depósito en efectivo',
    ticket_mercadopago: 'Depósito en efectivo',
    atm_mercadopago: 'Depósito en efectivo',
    account_money_mercadopago: 'Saldo en cuenta',
};

/**
 * Origen de la operación. Se muestra con alcance global, donde el listado trae
 * ventas de varias personas: sin esta etiqueta, una compra que el cliente hizo
 * por su cuenta o una venta de otro comercial se leerían como propias.
 *
 * Cada etiqueta responde "¿de quién es esta venta?", que es la primera línea de
 * la celda; en `other_agent` esa línea es el nombre del comercial y la etiqueta
 * baja a la segunda.
 */
export const ORIGIN_LABELS: Record<PanelTransactionOrigin, string> = {
    mine: 'Mía',
    other_agent: 'Otro comercial',
    commercial_unattributed: 'Sin comercial',
    self_service: 'Compra del cliente',
};

/** El "por qué" de cada etiqueta; viaja como tooltip de la celda. */
export const ORIGIN_HINTS: Record<PanelTransactionOrigin, string> = {
    mine: 'Venta que originaste desde el carrito comercial.',
    other_agent: 'La originó otro comercial, no el cliente por su cuenta.',
    commercial_unattributed:
        'Venta del carrito comercial sin comercial registrado: el cobro por Link de Pago de Mercado Pago todavía no guarda quién la hizo. Puede ser tuya o de otra persona.',
    self_service: 'El cliente la compró por su cuenta, sin comercial de por medio.',
};

/**
 * Detalle del estado crudo (`transactions.status`). Se muestra como apoyo del
 * estado agrupado, para que el comercial distinga "creada" de "pendiente" y
 * "cancelada" de "reembolsada".
 */
export const RAW_STATUS_LABELS: Record<number, string> = {
    0: 'Creada',
    1: 'Pendiente',
    2: 'Autorizada',
    3: 'Cancelada',
    4: 'Reembolsada',
};

/** Consulta base: Estado B (cartera del comercial), alcance propio. */
export const DEFAULT_QUERY: PanelTransactionsQuery = {
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    scope: 'mine',
    customerEmail: '',
    transactionId: null,
    paymentStatus: '',
    paymentMethod: '',
    yearMonth: DEFAULT_YEAR_MONTH,
};

/** Firma de una consulta; se usa como guard de recarga. */
export const buildQueryKey = (query: PanelTransactionsQuery): string =>
    [
        query.scope,
        query.customerEmail,
        query.transactionId ?? '',
        query.paymentStatus,
        query.paymentMethod,
        query.yearMonth,
        query.page,
        query.pageSize,
    ].join('|');
